/**
 * Measured Design System Preset
 *
 * Complete PrimeNG theme preset built on Aura base with custom token mappings.
 * This preset defines the visual identity of the Measured product suite.
 *
 * Architecture:
 * - Primitive tokens: Raw color palettes, spacing, typography, shadows, etc.
 * - Semantic tokens: Context-aware tokens (primary, surface, text, states)
 * - Component tokens: Per-component customizations for 55+ PrimeNG components
 *
 * Usage:
 * ```typescript
 * import { MeasuredPreset } from '@mds/themes';
 *
 * providePrimeNG({
 *   theme: {
 *     preset: MeasuredPreset,
 *     options: {
 *       darkModeSelector: '.app-dark', // For future dark mode support
 *     },
 *   },
 * })
 * ```
 */

import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

// Token imports
import {
  blueColors,
  grayColors,
  greenColors,
  redColors,
  yellowColors,
  orangeColors
} from '../tokens/primitive.tokens';
import { semanticTokens } from '../tokens/semantic.tokens';
import { componentTokens } from '../tokens/component';

// =============================================================================
// PRESET DEFINITION
// =============================================================================

/**
 * The Measured Design System Preset
 *
 * Extends PrimeNG's Aura theme with Measured's brand colors, typography,
 * and component customizations.
 */
export const MeasuredPreset = definePreset(Aura, {
  // ---------------------------------------------------------------------------
  // Primitive Tokens
  // ---------------------------------------------------------------------------
  // Map our color palettes to PrimeNG's expected primitive color names
  primitive: {
    // Primary brand color
    blue: blueColors,

    // Neutral/Surface colors
    gray: grayColors,

    // Semantic colors
    green: greenColors,
    red: redColors,
    yellow: yellowColors,
    orange: orangeColors
  },

  // ---------------------------------------------------------------------------
  // Semantic Tokens
  // ---------------------------------------------------------------------------
  // Context-aware tokens that define the design system's visual language
  semantic: semanticTokens,

  // ---------------------------------------------------------------------------
  // Component Tokens
  // ---------------------------------------------------------------------------
  // Per-component customizations for all PrimeNG components
  components: componentTokens
});

// =============================================================================
// PRESET UTILITIES
// =============================================================================

/**
 * Re-export preset utilities for runtime theme updates
 */
export {
  updatePreset,
  updatePrimaryPalette,
  updateSurfacePalette,
  usePreset,
  $dt
} from '@primeng/themes';

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type MeasuredPresetType = typeof MeasuredPreset;
